import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges
} from '@angular/core';
import {
  CarsWidget,
  CarsWidgetComponent
} from '@com-delta-idp/mnx-sitewide-header-ui';
import { AirportDropdownComponent } from '@com-delta-idp/mnx-sitewide-shared-core-smart';
import { PushPipe } from '@ngrx/component';
import { of, Subject } from 'rxjs';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  map,
  switchMap,
  takeUntil
} from 'rxjs/operators';
import { CarsLocationItem } from '../../../data-access/models/interface/booking-cars/booking-cars.interfaces';

import { BookingCarsBaseComponent } from './booking-cars-base.component';

@Component({
  selector: 'idp-booking-cars',
  standalone: true,
  imports: [
    CommonModule,
    CarsWidgetComponent,
    AirportDropdownComponent,
    PushPipe
  ],
  templateUrl: './booking-cars.component.html',
  styleUrls: ['./booking-cars.component.scss']
})
export class BookingCarsComponent
  extends BookingCarsBaseComponent
  implements OnInit, OnDestroy, OnChanges
{
  @Input() viewSearchOptionExpanded = false;
  @Input() isSelected = false;
  @Output() expandEmit = new EventEmitter<boolean>();

  locationSelected!: CarsLocationItem;
  emptyCarsContent = {} as CarsWidget;
  resetErrors = false;
  private searchSubject$ = new Subject<string>();

  ngOnInit(): void {
    this.addHeight();
    this.bookingCarsFacadeService.init(this.onInitComplete);
    this.setupSearchDebounce();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['isSelected'] &&
      !changes['isSelected'].firstChange &&
      !this.isSelected
    ) {
      this.resetErrors = true;
      this.openModalEvent = false;
      setTimeout(() => (this.resetErrors = false));
    }
  }

  ngOnDestroy(): void {
    this.cleanup();
  }

  private setupSearchDebounce(): void {
    this.searchSubject$
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        switchMap((searchTerm) =>
          this.bookingCarsFacadeService.searchLocations(searchTerm).pipe(
            catchError(() => {
              this.bookingCarsFacadeService.airportSearchErrorService.handleApiError(
                searchTerm
              );
              return of([]);
            }),
            map((locations) => ({ locations, searchTerm }))
          )
        ),
        takeUntil(this.destroy$)
      )
      .subscribe(({ locations, searchTerm }) => {
        this.bookingCarsFacadeService.adapter.setLocationsCache(locations);
        this.locationModalContent =
          this.bookingCarsFacadeService.adapter.createLocationModalContent(
            locations
          );
        this.bookingCarsFacadeService.airportSearchErrorService.handleEmptyResults(
          locations.length,
          searchTerm
        );
      });
  }

  openModal(value: boolean): void {
    this.openModalEvent = value;
    if (value && this.bookingCarsFacadeService.carsWidgetForm) {
      this.bookingCarsFacadeService.carsWidgetForm
        .get('locationCode')
        ?.markAsUntouched();
    }
  }

  onLocationSelected(location: { code: string; name: string }): void {
    const fullLocation =
      this.bookingCarsFacadeService.adapter.getLocationFromCache(location.code);
    if (fullLocation) {
      this.locationSelected = fullLocation;
    }
  }

  searchValue(searchTerm: string): void {
    this.bookingCarsFacadeService.airportSearchErrorService.clearError();
    this.searchSubject$.next(searchTerm);
  }

  formSubmitted(): void {
    const airportCode = this.locationSelected?.code || '';
    const gaiaId = this.locationSelected?.gaiaId || '';
    this.bookingCarsFacadeService.formSubmitted(airportCode, gaiaId);
  }

  handleAnalytics(element: HTMLElement): void {
    this.analyticsService.populateAnalyticsData(element);
  }
}
